import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit,
  Delete,
  Save,
  Cancel
} from '@mui/icons-material';
import { contactService } from '../services/customerService';

const CustomerContactsModal = ({ open, onClose, customerId, customerName }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    phone_number: '',
    ext: '',
    cell_phone_number: '',
    fax_number: '',
    email_address: '',
    is_ap_contact: false,
    is_active: true,
    notes: ''
  });

  useEffect(() => {
    if (open && customerId) {
      loadContacts();
    } else {
      setContacts([]);
      setEditingId(null);
      resetForm();
    }
  }, [open, customerId]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await contactService.getByCustomerId(customerId);
      setContacts(response.data || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
      setError(err.response?.data?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      phone_number: '',
      ext: '',
      cell_phone_number: '',
      fax_number: '',
      email_address: '',
      is_ap_contact: false,
      is_active: true,
      notes: ''
    });
    setEditingId(null);
    setError('');
  };

  const handleAdd = () => {
    resetForm();
    setEditingId('new');
  };

  const handleEdit = (contact) => {
    setFormData({
      name: contact.name || '',
      title: contact.title || '',
      phone_number: contact.phone_number || '',
      ext: contact.ext || '',
      cell_phone_number: contact.cell_phone_number || '',
      fax_number: contact.fax_number || '',
      email_address: contact.email_address || '',
      is_ap_contact: contact.is_ap_contact || false,
      is_active: contact.is_active !== false,
      notes: contact.notes || ''
    });
    setEditingId(contact.id);
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (editingId === 'new') {
        await contactService.create(customerId, formData);
      } else {
        await contactService.update(customerId, editingId, formData);
      }

      await loadContacts();
      resetForm();
    } catch (err) {
      console.error('Error saving contact:', err);
      setError(err.response?.data?.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (contactId) => {
    setContactToDelete(contactId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!contactToDelete) return;
    try {
      await contactService.delete(customerId, contactToDelete);
      await loadContacts();
      setDeleteDialogOpen(false);
      setContactToDelete(null);
    } catch (err) {
      console.error('Error deleting contact:', err);
      setError(err.response?.data?.message || 'Failed to delete contact');
      setDeleteDialogOpen(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [field]: value
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Customer Contacts - {customerName}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {editingId && (
              <Box sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {editingId === 'new' ? 'Add New Contact' : 'Edit Contact'}
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={formData.name}
                      onChange={handleInputChange('name')}
                      margin="dense"
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Title"
                      value={formData.title}
                      onChange={handleInputChange('title')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={formData.phone_number}
                      onChange={handleInputChange('phone_number')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Ext"
                      value={formData.ext}
                      onChange={handleInputChange('ext')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cell Phone"
                      value={formData.cell_phone_number}
                      onChange={handleInputChange('cell_phone_number')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Fax"
                      value={formData.fax_number}
                      onChange={handleInputChange('fax_number')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email_address}
                      onChange={handleInputChange('email_address')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.is_ap_contact}
                          onChange={handleInputChange('is_ap_contact')}
                          size="small"
                        />
                      }
                      label="AP Contact"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.is_active}
                          onChange={handleInputChange('is_active')}
                          size="small"
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={formData.notes}
                      onChange={handleInputChange('notes')}
                      margin="dense"
                      size="small"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSave}
                        disabled={saving || !formData.name}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Contacts ({contacts.length})
              </Typography>
              {!editingId && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                >
                  Add Contact
                </Button>
              )}
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                        No contacts found. Click "Add Contact" to add one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    contacts.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell>
                          {contact.name}
                          {contact.is_ap_contact && (
                            <Chip label="AP" size="small" color="primary" sx={{ ml: 0.5, height: '18px', fontSize: '0.7rem' }} />
                          )}
                        </TableCell>
                        <TableCell>{contact.title || '-'}</TableCell>
                        <TableCell>
                          {contact.phone_number || '-'}
                          {contact.ext && ` x${contact.ext}`}
                        </TableCell>
                        <TableCell>{contact.email_address || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={contact.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={contact.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(contact)}
                            disabled={editingId !== null}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(contact.id)}
                            disabled={editingId !== null}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">
          Close
        </Button>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setContactToDelete(null);
        }}
        aria-labelledby="delete-contact-dialog-title"
        aria-describedby="delete-contact-dialog-description"
      >
        <DialogTitle id="delete-contact-dialog-title">
          Delete Contact?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-contact-dialog-description">
            Are you sure you want to delete this contact? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setContactToDelete(null);
          }} color="primary" size="small">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" size="small" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CustomerContactsModal;
